import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import styled from 'styled-components';

import SpinnerComponent from '../Common/Spinners';

const PageTitle = styled.div`
  font-size: 1.2em;
  font-weight: bold;
  padding-bottom: 3px;
  border-bottom: 1px solid black;
`

const SpinnerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 15em;
`

const TournamentStatsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 20em;
  max-height: 30em;
  overflow-y: scroll;
  font-size: 0.8em;
`

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  font-weight: bold;
  margin-top: 1em;
  margin-bottom: 0.5em;
  padding-bottom: 3px;
  border-bottom: 1px solid #666;
`

const HeaderBox = styled.div`
  flex: 1;
  ${props => props.$minWidth && `min-width: ${props.$minWidth};`}
  display: flex;
  flex-direction: row;
  justify-content: ${props => props.align === 'left' ? 'flex-start' : 'center'};
  align-items: center;
`

const RowContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  max-height: 2em;
`

const RowBox = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: ${props => props.align === 'left' ? 'flex-start' : 'center'};
  align-items: center;
  height: 2em;
  ${props => props.$minWidth && `min-width: ${props.$minWidth};`}
  color: black;
  background-color: ${props => props.$hovered ? 'rgb(213, 233, 247)' : props.$rowIndex % 2 > 0 ? '#ccc' : 'white'};
`

const RAR = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: ${props => props.align === 'left' ? 'flex-start' : 'center'};
  align-items: center;
  height: 2em;
  color: black;
  background-color: ${props => props.$hovered ? 'rgb(213, 233, 247)' : props.$rowIndex % 2 > 0 ? '#ccc' : 'white'};
`

const Smaller = styled.span`
  font-size: .8em;
`

const TournamentRow = ({ row, rowIndex }) => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
    ? true
    : false;

  const [hovered, setHovered] = useState(false);

  return (
    <RowContainer
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <RowBox align='left' $minWidth='14em' $hovered={hovered} $rowIndex={rowIndex}>
        {row.tournamentName}
      </RowBox>
      <RowBox $hovered={hovered} $rowIndex={rowIndex}>{row.gameWins + row.gameLosses}</RowBox>
      <RowBox $hovered={hovered} $rowIndex={rowIndex}>{(row.roundWins / row.totalRounds * 100).toFixed(0)}%</RowBox>
      <RAR $hovered={hovered} $rowIndex={rowIndex} rar={row.totalRAR / row.totalRounds}>
        {(row.totalRAR / row.totalRounds) > 0 && '+'}{(row.totalRAR / row.totalRounds).toFixed(1)}
      </RAR>
      <RowBox $hovered={hovered} $rowIndex={rowIndex}>
        {(row.totalKastPlusMinus / row.totalRounds) > 0 && '+'}{(row.totalKastPlusMinus / row.totalRounds).toFixed(1)}
        <Smaller>%</Smaller>
      </RowBox>
      {!isMobile && (
        <>
          <RowBox $hovered={hovered} $rowIndex={rowIndex}>{(row.totalFk / row.totalRounds * 100).toFixed(1)}<Smaller>%</Smaller></RowBox>
          <RowBox $hovered={hovered} $rowIndex={rowIndex}>{(row.totalUfd / row.totalRounds * 100).toFixed(1)}<Smaller>%</Smaller></RowBox>
        </>
      )}
    </RowContainer>
  )
};

const StatsBySplit = ({ AppStore, playerId }) => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
    ? true
    : false;

  const fetching = AppStore.playerMatchHistoryFetching.get() || AppStore.playerStatsFetching.get();
  const statsBySplit = AppStore.playerProfileDataMap[playerId].statsBySplit;

  useEffect(() => {
    const callConstructStatsBySplit = (async () => {
      AppStore.setPlayerStatsFetching(true);
      await AppStore.constructStatsBySplit(playerId);
      AppStore.setPlayerStatsFetching(false);
    })

    if (AppStore.playerProfileDataMap[playerId]?.matchHistory && !statsBySplit) {
      callConstructStatsBySplit();
    }
  }, [
    AppStore,
    playerId,
    fetching,
    statsBySplit
  ]);

  return (
    <>
      <PageTitle>Stats by Split</PageTitle>
      <TournamentStatsWrapper>
        {
          (fetching) ? (
            <SpinnerWrapper>
              <SpinnerComponent />
            </SpinnerWrapper>
          ) : (
            !statsBySplit ? (
              <div>No stats available</div>
            ) : (
              <>
                <Header>
                  <HeaderBox align='left' $minWidth='14em'>Split</HeaderBox>
                  <HeaderBox>GP</HeaderBox>
                  <HeaderBox>RndWR</HeaderBox>
                  <HeaderBox>RAR</HeaderBox>
                  <HeaderBox>KAST+-</HeaderBox>
                  {!isMobile && (
                    <>
                      <HeaderBox>FK<Smaller>%</Smaller></HeaderBox>
                      <HeaderBox>UFD<Smaller>%</Smaller></HeaderBox>
                    </>
                  )}
                </Header>
                {
                  statsBySplit.length > 0 && statsBySplit.map((row, idx) => {
                    return <TournamentRow row={row} key={`${row.tournamentId}`} rowIndex={idx} />
                  })
                }
              </>
            )
          )}
      </TournamentStatsWrapper>
    </>
  );
};

export default inject('AppStore')(observer(StatsBySplit))